<script lang="ts">
  import { scheduleUndo } from '$lib/state/actionDialog.svelte';
  import { createQuery, createMutation, useQueryClient } from '@tanstack/svelte-query';
  import { Plus, Trash2 } from 'lucide-svelte';
  import { adminLeadRoutesApi, adminLeadSettingsApi, adminVerticalsApi } from '$lib/api/admin';
  import { getErrorMessage } from '$lib/api/client';
  import Modal from '$lib/components/ui/Modal.svelte';
  import type { LeadRoute } from '$lib/types';

  const queryClient = useQueryClient();
  const EMAIL_ONLY = 'email-only';

  const verticalsQuery = createQuery(() => ({
    queryKey: ['admin-verticals'],
    queryFn: () => adminVerticalsApi.list()
  }));
  const routesQuery = createQuery(() => ({
    queryKey: ['admin-lead-routes'],
    queryFn: () => adminLeadRoutesApi.list()
  }));
  const settingsQuery = createQuery(() => ({
    queryKey: ['admin-lead-settings'],
    queryFn: () => adminLeadSettingsApi.get()
  }));

  function verticalName(id: number | null): string {
    if (id === null) return 'Email only';
    return verticalsQuery.data?.results.find((v) => v.id === id)?.name ?? `#${id}`;
  }

  // --- routing table ---

  let showForm = $state(false);
  let editingId = $state<number | null>(null);
  let formError = $state('');

  function emptyForm() {
    return {
      category: '',
      vertical: EMAIL_ONLY as string | number,
      notify_emails: '',
      extra_verticals: [] as number[]
    };
  }
  let form = $state(emptyForm());

  function toggleExtraVertical(id: number) {
    form.extra_verticals = form.extra_verticals.includes(id)
      ? form.extra_verticals.filter((v) => v !== id)
      : [...form.extra_verticals, id];
  }

  const saveMutation = createMutation(() => ({
    mutationFn: (body: Record<string, unknown>) =>
      editingId ? adminLeadRoutesApi.update(editingId, body) : adminLeadRoutesApi.create(body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-lead-routes'] });
      closeForm();
    },
    onError: (err: unknown) => (formError = getErrorMessage(err))
  }));

  const deleteMutation = createMutation(() => ({
    mutationFn: (id: number) => adminLeadRoutesApi.remove(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['admin-lead-routes'] })
  }));

  function openCreate() {
    editingId = null;
    form = emptyForm();
    formError = '';
    showForm = true;
  }

  function openEdit(row: LeadRoute) {
    editingId = row.id;
    form = {
      category: row.category,
      vertical: row.vertical_id ?? EMAIL_ONLY,
      notify_emails: row.notify_emails.join(', '),
      extra_verticals: [...row.extra_vertical_ids]
    };
    formError = '';
    showForm = true;
  }

  function closeForm() {
    showForm = false;
  }

  function submit(e: SubmitEvent) {
    e.preventDefault();
    if (!form.category.trim()) {
      formError = 'Category is required';
      return;
    }
    formError = '';
    saveMutation.mutate({
      category: form.category,
      vertical_id: form.vertical === EMAIL_ONLY ? null : Number(form.vertical),
      notify_emails: form.notify_emails
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean),
      extra_vertical_ids: form.extra_verticals.filter((id) => id !== Number(form.vertical))
    });
  }

  function remove(row: LeadRoute) {
    scheduleUndo(`Delete route "${row.category}"`, () => deleteMutation.mutate(row.id));
  }

  // --- default/fallback settings ---

  let settingsForm = $state({
    default_vertical: EMAIL_ONLY as string | number,
    default_notify_emails: '',
    global_fallback_email: '',
    support_ticket_notify_emails: ''
  });
  let settingsError = $state('');
  let settingsLoaded = $state(false);

  $effect(() => {
    if (!settingsLoaded && settingsQuery.data) {
      settingsForm = {
        default_vertical: settingsQuery.data.default_vertical_id ?? EMAIL_ONLY,
        default_notify_emails: settingsQuery.data.default_notify_emails.join(', '),
        global_fallback_email: settingsQuery.data.global_fallback_email.join(', '),
        support_ticket_notify_emails: settingsQuery.data.support_ticket_notify_emails.join(', ')
      };
      settingsLoaded = true;
    }
  });

  const settingsMutation = createMutation(() => ({
    mutationFn: (body: Record<string, unknown>) => adminLeadSettingsApi.update(body),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['admin-lead-settings'] }),
    onError: (err: unknown) => (settingsError = getErrorMessage(err))
  }));

  function submitSettings(e: SubmitEvent) {
    e.preventDefault();
    settingsError = '';
    settingsMutation.mutate({
      default_vertical_id: settingsForm.default_vertical === EMAIL_ONLY ? null : Number(settingsForm.default_vertical),
      default_notify_emails: settingsForm.default_notify_emails
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean),
      global_fallback_email: settingsForm.global_fallback_email
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean),
      support_ticket_notify_emails: settingsForm.support_ticket_notify_emails
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean)
    });
  }
</script>

<div>
  <div class="mb-[18px]">
    <h2 class="tym-section-title">Lead routing</h2>
    <div class="tym-section-sub">Map website form categories to verticals</div>
  </div>

  <div class="mb-8">
    <div class="mb-4 flex items-center justify-between">
      <h3 class="font-heading text-[15px] font-bold text-navy">Category routing</h3>
      <button type="button" class="btn-primary" onclick={openCreate}>
        <Plus class="h-4 w-4" /> New route
      </button>
    </div>

    {#if routesQuery.isLoading}
      <p class="tym-loading-state">Loading routes&hellip;</p>
    {:else if routesQuery.isError}
      <p class="tym-error-state">{getErrorMessage(routesQuery.error)}</p>
    {:else if routesQuery.data}
      <div class="tym-table-wrap max-w-[720px]">
        <div class="tym-table-scroll">
          <table class="tym-table">
            <thead>
              <tr>
                <th>Form category</th>
                <th>Routes to</th>
                <th class="num"></th>
              </tr>
            </thead>
            <tbody>
              {#each routesQuery.data.results as row (row.id)}
                <tr>
                  <td class="font-medium text-navy">{row.category}</td>
                  <td>
                    <span class="inline-flex items-center gap-1.5 text-[13px] font-semibold text-navy">
                      <span
                        class="h-2 w-2 shrink-0 rounded-full"
                        style="background:{row.vertical_id ? '#226A73' : '#C9CDCE'}"
                      ></span>
                      {verticalName(row.vertical_id)}
                    </span>
                    {#if row.extra_vertical_ids.length}
                      <div class="mt-1 flex flex-wrap gap-1">
                        {#each row.extra_vertical_ids as id (id)}
                          <span class="tym-badge" title="Also visible to">{verticalName(id)}</span>
                        {/each}
                      </div>
                    {/if}
                    {#if row.notify_emails.length}
                      <div class="mt-0.5 text-[11.5px] text-dark-gray">{row.notify_emails.join(', ')}</div>
                    {/if}
                  </td>
                  <td class="num">
                    <div class="flex justify-end gap-1.5">
                      <button type="button" class="btn-secondary !h-9" onclick={() => openEdit(row)}>Edit</button>
                      <button
                        type="button"
                        class="btn-danger !h-10 !w-10 !p-0"
                        onclick={() => remove(row)}
                        aria-label={`Delete route for ${row.category}`}
                      >
                        <Trash2 class="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              {:else}
                <tr><td class="empty" colspan="3">No routes configured.</td></tr>
              {/each}
            </tbody>
          </table>
        </div>
      </div>
    {/if}
  </div>

  <div class="border-t border-toyama-cloudy-gray pt-6">
    <h3 class="mb-4 font-heading text-[15px] font-bold text-navy">Unknown categories &amp; global fallback</h3>
    {#if settingsQuery.isLoading}
      <p class="tym-loading-state">Loading settings&hellip;</p>
    {:else}
      <form class="space-y-4 max-w-lg" onsubmit={submitSettings}>
        <div>
          <label class="label" for="default-vertical">Default route for unknown categories</label>
          <select id="default-vertical" class="input" bind:value={settingsForm.default_vertical}>
            <option value={EMAIL_ONLY}>Email only</option>
            {#each verticalsQuery.data?.results ?? [] as v (v.id)}
              <option value={v.id}>{v.name}</option>
            {/each}
          </select>
        </div>
        <div>
          <label class="label" for="default-notify">Default notify emails (comma-separated)</label>
          <input id="default-notify" class="input" bind:value={settingsForm.default_notify_emails} />
        </div>
        <div>
          <label class="label" for="global-fallback">Global fallback email (comma-separated)</label>
          <input id="global-fallback" class="input" bind:value={settingsForm.global_fallback_email} />
          <p class="text-xs text-dark-gray mt-1">
            Used whenever a category (or the default route above) has no notify emails of its own.
          </p>
        </div>
        <div>
          <label class="label" for="support-ticket-notify">Support ticket notify emails (comma-separated)</label>
          <input id="support-ticket-notify" class="input" bind:value={settingsForm.support_ticket_notify_emails} />
          <p class="text-xs text-dark-gray mt-1">
            External recipients for WizHom Pro support tickets, alongside any per-user subscriptions in Users.
          </p>
        </div>
        {#if settingsError}<p class="text-sm text-red-600">{settingsError}</p>{/if}
        <button type="submit" class="btn-primary" disabled={settingsMutation.isPending}>
          {settingsMutation.isPending ? 'Saving...' : 'Save'}
        </button>
      </form>
    {/if}
  </div>
</div>

{#if showForm}
  <Modal title={editingId ? 'Edit route' : 'New route'} onClose={closeForm}>
    <form class="space-y-4" onsubmit={submit}>
      <div>
        <label class="label" for="category">Category (matched case-insensitively)</label>
        <input id="category" class="input" bind:value={form.category} />
      </div>
      <div>
        <label class="label" for="vertical">Routes to</label>
        <select id="vertical" class="input" bind:value={form.vertical}>
          <option value={EMAIL_ONLY}>Email only</option>
          {#each verticalsQuery.data?.results ?? [] as v (v.id)}
            <option value={v.id}>{v.name}</option>
          {/each}
        </select>
      </div>
      <div>
        <label class="label" for="notify">Notify emails (comma-separated, optional)</label>
        <input id="notify" class="input" bind:value={form.notify_emails} placeholder="falls back to global default" />
      </div>
      <div>
        <span class="label">Also visible to (in addition to "Routes to" above)</span>
        <div class="space-y-1.5">
          {#each verticalsQuery.data?.results.filter((v) => v.id !== Number(form.vertical)) ?? [] as v (v.id)}
            <label class="flex items-center gap-2 text-[13px] text-navy">
              <input
                type="checkbox"
                checked={form.extra_verticals.includes(v.id)}
                onchange={() => toggleExtraVertical(v.id)}
              />
              {v.name}
            </label>
          {/each}
        </div>
      </div>

      {#if formError}<p class="text-sm text-red-600">{formError}</p>{/if}

      <div class="flex justify-end gap-2 pt-2">
        <button type="button" class="btn-secondary" onclick={closeForm}>Cancel</button>
        <button type="submit" class="btn-primary" disabled={saveMutation.isPending}>
          {saveMutation.isPending ? 'Saving...' : 'Save'}
        </button>
      </div>
    </form>
  </Modal>
{/if}
